import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { CODES, shipmentsApi } from '../../utils/shipmentsApi';
import { EMPTY_FORM } from './shipmentForm';
import AddShipmentDialog from './AddShipmentDialog';
import ReviewApproveDialog from './ReviewApproveDialog';
import ReviewCard from './ReviewCard';
import ReviewLinkDialog from './ReviewLinkDialog';
import Pager from './Pager';
import { Banner, EmptyState, SkeletonBlock } from './Ui';
import { useRemote } from './useRemote';

const PAGE = 10;

// Prefill of "Vytvořit zásilku" from what the AI extracted
function createInitial(item) {
  const x = item.extraction || {};
  return {
    ...EMPTY_FORM,
    direction: ['prichozi', 'odchozi'].includes(item.direction) ? item.direction : 'prichozi',
    carrierCode: typeof x.carrier === 'string' ? x.carrier : '',
    trackingNumber: Array.isArray(x.trackingNumbers) ? String(x.trackingNumbers[0] || '') : '',
    orderRef: typeof x.orderRef === 'string' ? x.orderRef : '',
    description: typeof x.description === 'string' ? x.description : '',
    expectedDelivery: typeof x.expectedDeliveryDate === 'string' ? x.expectedDeliveryDate.slice(0, 10) : '',
  };
}

// Review queue (GET /review): decisions are one click, the card disappears at once and comes back if the request fails.
const ReviewQueue = ({ notify, onChanged }) => {
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [deletedFor, setDeletedFor] = useState({}); // emailId -> shipment id reported as deleted by a 409 SHIPMENT_DELETED
  const [dialog, setDialog] = useState(null); // { type: 'correct' | 'link' | 'create', item }
  const review = useRemote(`review:${page}`, () => shipmentsApi.reviewList({ limit: PAGE, offset: (page - 1) * PAGE }));
  const items = review.data?.items || [];
  const total = review.data?.total || 0;

  const removeLocally = (emailId) => review.mutate((d) => (d ? { ...d, items: d.items.filter((i) => i.emailId !== emailId), total: Math.max(0, d.total - 1) } : d));

  // optimistic: remove first, restore (reload) when the server refuses
  const run = async (item, request, successText) => {
    setBusyId(item.emailId);
    setDialog(null);
    const index = Math.max(0, items.findIndex((i) => i.emailId === item.emailId));
    removeLocally(item.emailId);
    try {
      const result = await request();
      notify.success(typeof successText === 'function' ? successText(result) : successText);
      onChanged?.();
      if (items.length <= 1 && page > 1) setPage((p) => p - 1);
      else review.reload(); // refill the page from the server
    } catch (err) {
      if (err.code === CODES.SHIPMENT_DELETED) {
        // 409: the shipment was deleted meanwhile. The card is put back on the same place with the "restore it first"
        // hint and a link (not with a generic error toast), whether or not the server still lists the mail.
        const id = Number(err.data?.shipmentId) || Number(item.shipmentId) || 0;
        setDeletedFor((m) => ({ ...m, [item.emailId]: id }));
        review.mutate((d) => {
          if (!d) return d;
          const rest = d.items.filter((i) => i.emailId !== item.emailId);
          return { ...d, items: [...rest.slice(0, index), item, ...rest.slice(index)], total: d.total + 1 };
        });
      } else {
        notify.error(err.message);
        review.reload();
      }
    } finally {
      setBusyId(null);
    }
  };

  const approve = (item, corrections) => run(item, () => shipmentsApi.reviewApprove(item.emailId, corrections),
    (r) => (r?.newlyApplied > 0 ? `Schváleno, nových událostí: ${r.newlyApplied}.` : 'E-mail byl schválen.'));
  const reject = (item) => run(item, () => shipmentsApi.reviewReject(item.emailId), 'E-mail byl zamítnut.');
  const link = (item, shipment) => run(item, () => shipmentsApi.reviewLink(item.emailId, { shipmentId: shipment.id }), 'E-mail byl přiřazen k zásilce.');

  if (review.error && !review.data) {
    return (
      <Banner tone="bad" action={<button type="button" className="shp-textbtn" onClick={review.reload}>Zkusit znovu</button>}>
        Frontu ke kontrole se nepodařilo načíst. {review.error.message}
      </Banner>
    );
  }
  if (!review.loaded) return <SkeletonBlock height={220} />;
  if (items.length === 0) {
    return <EmptyState icon={CheckCircle2} title="Ke kontrole nic nečeká">Všechny e-maily jsou zpracované. Nové se objeví, jakmile je synchronizace označí jako nejisté.</EmptyState>;
  }

  return (
    <>
      <p className="shp-sr" role="status">Ke kontrole: {total}</p>
      <div className={`shp-review ${review.loading ? 'shp-dim' : ''}`}>
        {items.map((item) => (
          <ReviewCard
            key={item.emailId} item={item} busy={busyId === item.emailId} deletedId={deletedFor[item.emailId] || 0}
            onApprove={() => approve(item)} onCorrect={() => setDialog({ type: 'correct', item })}
            onReject={() => reject(item)} onLink={() => setDialog({ type: 'link', item })} onCreate={() => setDialog({ type: 'create', item })}
          />
        ))}
      </div>
      <Pager total={total} page={page} pageSize={PAGE} onPage={setPage} />

      {dialog?.type === 'correct' && <ReviewApproveDialog onClose={() => setDialog(null)} onSubmit={(c) => approve(dialog.item, c)} />}
      {dialog?.type === 'link' && <ReviewLinkDialog onClose={() => setDialog(null)} onSubmit={(s) => link(dialog.item, s)} />}
      {dialog?.type === 'create' && (
        <AddShipmentDialog
          title="Vytvořit zásilku z e-mailu" submitLabel="Vytvořit zásilku" initial={createInitial(dialog.item)}
          submit={(body) => shipmentsApi.reviewLink(dialog.item.emailId, { create: body })}
          onClose={() => setDialog(null)}
          onCreated={() => { removeLocally(dialog.item.emailId); setDialog(null); notify.success('Zásilka byla vytvořena.'); onChanged?.(); review.reload(); }}
        />
      )}
    </>
  );
};

export default ReviewQueue;
